'use client';

import { SliderArrow } from '@/components/SliderArrow';
import { Default_Image, base64Image } from '@/constants';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { ICategoryBrandCarouselItemsProperty, ICategoryBrandCarouselProperty } from '@/interfaces/homepage';
import { useEffect, useState } from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';

import { IconArrowLeftDouble } from '@/icons/IconArrowLeftDouble';
import { IconArrowRightDouble } from '@/icons/IconArrowRightDouble';
import Image from 'next/image';
import CustomLink from '@/components/CustomLink';

const CategoryCarousel = ({
  className,
  carouselContent,
  autoPlayTimer,
  dataTestId = 'category-carousel',
}: {
  className: string;
  carouselContent?: ICategoryBrandCarouselProperty;
  autoPlayTimer: number;
  dataTestId?: string;
}) => {
  const [swiper, setSwiper] = useState<any>(null);
  const [isMounted, setIsMounted] = useState<boolean>(false);

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-100px',
    threshold: 0.0,
  });

  useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
      containerRef?.current?.swiper?.autoplay.start();
    } else {
      containerRef?.current?.swiper?.autoplay.stop();
    }
  }, [isVisible, containerRef]);

  const swiperInternalComponent = (items?: ICategoryBrandCarouselItemsProperty[]) => {
    return (
      <Swiper
        key={isMounted.toString()}
        ref={containerRef}
        onSwiper={(s) => {
          setSwiper(s);
        }}
        slidesPerView={1}
        spaceBetween={4}
        modules={[Navigation, Autoplay]}
        autoplay={{
          delay: autoPlayTimer - 1000,
        }}
        breakpoints={{
          400: {
            slidesPerView: 2,
            autoplay: {
              delay: autoPlayTimer - 1000,
            },
          },
          640: {
            slidesPerView: 4,
            autoplay: {
              delay: autoPlayTimer,
            },
          },
        }}
        navigation={{
          prevEl: '.category-swipe-prev',
          nextEl: '.category-swipe-next',
        }}
        wrapperClass="flex"
        className="!-mx-0.5 sm:!-mx-2"
        loop
        data-testid={`${dataTestId}-swiper`}
      >
        {items?.map((item, index) => (
          <SwiperSlide key={item?.SeName + index} data-testid={`${dataTestId}-slide-${index}`}>
            <CustomLink
              href={`/${carouselContent?.DisplayType?.toLowerCase()}/${item?.SeName}`}
              className="block"
              data-testid={`${dataTestId}-link-${index}`}
            >
              <div className="relative block aspect-square w-full object-cover px-0.5 sm:px-2">
                <Image
                  src={item?.ImageUrl !== '' ? item.ImageUrl : Default_Image}
                  alt={item?.AltText ? item.AltText : item?.Name}
                  fill
                  quality={70}
                  placeholder="blur"
                  blurDataURL={base64Image}
                  unoptimized={true}
                  data-testid={`${dataTestId}-image-${index}`}
                />
              </div>
              <div className="mx-6 mt-1" dangerouslySetInnerHTML={{ __html: item?.Content }} data-testid={`${dataTestId}-content-${index}`} />
            </CustomLink>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  return (
    <div className={`${className}`} data-testid={dataTestId}>
      <div className="relative" data-testid={`${dataTestId}-container`}>
        {isMounted
          ? swiperInternalComponent(carouselContent?.Items)
          : swiperInternalComponent(
              carouselContent?.Items && carouselContent?.Items?.length > 4 ? carouselContent?.Items?.slice(0, 4) : carouselContent?.Items,
            )}
        <div className="arrow-design category-swipe-prev swipe-prev absolute inset-0 aspect-square" data-testid={`${dataTestId}-prev-arrow`}>
          <SliderArrow className="left-3 top-1/2 -translate-y-1/2" data-testid={`${dataTestId}-prev-arrow-button`}>
            <IconArrowLeftDouble className="h-8 w-8" />
          </SliderArrow>
        </div>
        <div className="arrow-design category-swipe-next swipe-next absolute inset-0 aspect-square" data-testid={`${dataTestId}-next-arrow`}>
          <SliderArrow className="right-3 top-1/2 -translate-y-1/2" data-testid={`${dataTestId}-next-arrow-button`}>
            <IconArrowRightDouble className="h-8 w-8" />
          </SliderArrow>
        </div>
      </div>
      <style jsx>{`
        @media (min-width: 400px) {
          .arrow-design {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default CategoryCarousel;
